import { useCallback, useEffect, useState } from "react";
import type { FilterValues } from "@/components/filters/FilterBar";
import type { TdmCategory, TdmMetric, TdmSector } from "@/types/tdm";

/** Un término de búsqueda del TDM; apagado sigue en la lista pero no matchea. */
export interface TdmKeyword {
  term: string;
  active: boolean;
}

/** Estado completo de filtros de la pestaña TDM (persistido en localStorage). */
export interface TdmFilterState extends FilterValues {
  keywords: TdmKeyword[];
  metric: TdmMetric;
  activeCategory: TdmCategory; // categoría visible en modo "cantidad"
  categories: TdmCategory[]; // filtro multiselect (vacío = todas)
  decentralizeLima: boolean; // reubica las obras de Lima que están en otro depto
  sectors: TdmSector[]; // filtro multiselect (vacío = todos)
}

const STORAGE_KEY = "tdm:filters:v1";

export const TDM_DEFAULT_FILTERS: TdmFilterState = {
  // Compartidos con FilterBar
  departmentIds: [],
  provinceIds: [],
  districtIds: [],
  dateFrom: "",
  dateTo: "",
  entityIds: [],
  subjects: [],
  stages: [],
  maxDays: null,
  onlyInterests: false,
  // Específicos de TDM
  keywords: [{ term: "asf", active: true }],
  metric: "amount",
  activeCategory: "LIQUIDO",
  categories: [],
  sectors: [],
  decentralizeLima: false,
};

function load(): TdmFilterState {
  if (typeof window === "undefined") return TDM_DEFAULT_FILTERS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return TDM_DEFAULT_FILTERS;
    // Merge sobre los defaults para tolerar versiones/campos faltantes.
    const saved = JSON.parse(raw) as Partial<TdmFilterState> & {
      keyword?: string;
    };
    const { keyword, ...rest } = saved;
    const merged = { ...TDM_DEFAULT_FILTERS, ...rest };
    // El estado viejo guardaba un único `keyword`: se conserva como pill.
    if (!saved.keywords?.length && keyword?.trim())
      merged.keywords = [{ term: keyword, active: true }];
    return merged;
  } catch {
    return TDM_DEFAULT_FILTERS;
  }
}

/**
 * Filtros TDM con persistencia en el navegador. No hay perfiles: el estado se
 * guarda directamente y se restaura al volver a la pestaña.
 */
export function useTdmFilters() {
  const [filters, setFilters] = useState<TdmFilterState>(load);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    } catch {
      /* almacenamiento no disponible: se ignora */
    }
  }, [filters]);

  const patch = useCallback((p: Partial<TdmFilterState>) => {
    setFilters((prev) => ({ ...prev, ...p }));
  }, []);

  const reset = useCallback(() => setFilters(TDM_DEFAULT_FILTERS), []);

  return { filters, patch, setFilters, reset };
}

/** Traduce el estado de filtros al query string que espera el backend `/tdm`. */
export function tdmFiltersToParams(
  f: TdmFilterState,
): Record<string, string | number | (string | number)[] | undefined> {
  return {
    // Solo los activos: apagar una pill es lo mismo que no tenerla.
    keywords: f.keywords.filter((k) => k.active).map((k) => k.term),
    departmentId: f.departmentIds,
    provinceId: f.provinceIds,
    districtId: f.districtIds,
    procuringEntityId: f.entityIds,
    dateFrom: f.dateFrom || undefined,
    dateTo: f.dateTo || undefined,
    stage: f.stages,
    subject: f.subjects,
    contratadomaxdays: f.maxDays ?? undefined,
    sectors: f.sectors,
    categories: f.categories,
    decentralizeLima: f.decentralizeLima ? "true" : undefined,
  };
}
